import { useState } from 'react';
import type { ReactNode } from 'react';

const ICONS: Record<string, string> = {
  waiting: 'M4 13l2-8h12l2 8M4 13v6h16v-6M4 13h5l1 2h4l1-2h5',
  now: 'M12 12m-2 0a2 2 0 1 0 4 0a2 2 0 1 0-4 0M7.5 7.5a6 6 0 0 0 0 9M16.5 7.5a6 6 0 0 1 0 9M4.5 4.5a10 10 0 0 0 0 15M19.5 4.5a10 10 0 0 1 0 15',
  issues: 'M9 9a3 3 0 0 1 6 0v1H9zM8 10h8v5a4 4 0 0 1-8 0zM4 13h4M16 13h4M5 7l3 2M19 7l-3 2M5 19l3-2M19 19l-3-2',
  projects: 'M4 4h7v7H4zM13 4h7v7h-7zM4 13h7v7H4zM13 13h7v7h-7z',
  brief: 'M12 16a4 4 0 1 0 0-8a4 4 0 0 0 0 8M12 3v2M12 19v2M3 12h2M19 12h2M5.6 5.6L7 7M17 17l1.4 1.4M5.6 18.4L7 17M17 7l1.4-1.4',
  assistant: 'M4 5h16v11H9l-5 4zM8 9h8M8 12h5',
  settings: 'M12 15a3 3 0 1 0 0-6a3 3 0 0 0 0 6M19 12a7 7 0 0 0-.1-1.2l2-1.5-2-3.4-2.3.9a7 7 0 0 0-2-1.2L14.2 3h-4.4l-.4 2.6a7 7 0 0 0-2 1.2l-2.3-.9-2 3.4 2 1.5A7 7 0 0 0 5 12c0 .4 0 .8.1 1.2l-2 1.5 2 3.4 2.3-.9a7 7 0 0 0 2 1.2l.4 2.6h4.4l.4-2.6a7 7 0 0 0 2-1.2l2.3.9 2-3.4-2-1.5c.1-.4.1-.8.1-1.2',
  refresh: 'M20 11a8 8 0 1 0-2.3 5.7M20 4v7h-7',
  chevron: 'M9 6l6 6-6 6',
  info: 'M12 21a9 9 0 1 0 0-18a9 9 0 0 0 0 18M12 11v5M12 8h.01',
  eye: 'M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12M12 15a3 3 0 1 0 0-6a3 3 0 0 0 0 6',
  eyeoff: 'M3 3l18 18M10.6 6.1A9.8 9.8 0 0 1 12 5c6 0 10 7 10 7a17 17 0 0 1-3.2 3.8M6.5 6.9C3.7 8.7 2 12 2 12s4 7 10 7a9.6 9.6 0 0 0 4-.9M9.9 9.9a3 3 0 0 0 4.2 4.2',
  up: 'M12 19V5M5 12l7-7 7 7',
  down: 'M12 5v14M5 12l7 7 7-7',
  top: 'M5 4h14M12 20V8M6 14l6-6 6 6',
  plus: 'M12 5v14M5 12h14',
  check: 'M5 12l5 5L20 7',
  x: 'M6 6l12 12M18 6L6 18',
  filter: 'M3 5h18l-7 8v6l-4-2v-4z',
  brain: 'M12 4a3 3 0 1 0 0 .01M5 16a3 3 0 1 0 0 .01M19 16a3 3 0 1 0 0 .01M12 7v4M12 11l-5 3M12 11l5 3M8 16h8',
};
export type IconName = keyof typeof ICONS;

export function Icon({ name, size = 18 }: { name: IconName; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={ICONS[name]} /></svg>
  );
}

export function Spinner({ label }: { label?: string }) {
  return <div className="spinner-row"><span className="spinner" aria-hidden="true" />{label ? <span className="muted">{label}</span> : null}</div>;
}

export function ErrorNote({ text }: { text: string | null | undefined }) {
  return text ? <div className="error-note" role="alert">{text}</div> : null;
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="empty">{children}</div>;
}

export function Chip({ children, tone = 'neutral', title }: { children: ReactNode; tone?: 'neutral' | 'accent' | 'red' | 'orange' | 'green' | 'yellow'; title?: string }) {
  return <span className={`chip chip-${tone}`} title={title}>{children}</span>;
}

export function Segmented<T extends string>({ value, options, onChange, label }: {
  value: T; options: { id: T; label: string }[]; onChange: (v: T) => void; label: string;
}) {
  return (
    <div className="segmented" role="tablist" aria-label={label}>
      {options.map((o) => (
        <button key={o.id} role="tab" aria-selected={value === o.id} className={value === o.id ? 'on' : ''} onClick={() => onChange(o.id)}>{o.label}</button>
      ))}
    </div>
  );
}

export function SectionTitle({ children, count }: { children: ReactNode; count?: number }) {
  return <h3 className="section-title">{children}{count != null ? <span className="count">{count}</span> : null}</h3>;
}

export function PageHeader({ title, children }: { title: string; children?: ReactNode }) {
  return <header className="page-header"><h1>{title}</h1><div className="spacer" />{children}</header>;
}

/** Password-style field with a show/hide toggle (house rule: every secret field has one). */
export function SecretField({ value, onChange, placeholder, autoComplete, id }: {
  value: string; onChange: (v: string) => void; placeholder?: string; autoComplete?: string; id?: string;
}) {
  return (
    <SecretInner value={value} onChange={onChange} placeholder={placeholder} autoComplete={autoComplete} id={id} />
  );
}
function SecretInner({ value, onChange, placeholder, autoComplete, id }: {
  value: string; onChange: (v: string) => void; placeholder?: string; autoComplete?: string; id?: string;
}) {
  const [shown, setShown] = useState(false);
  return (
    <div className="secret">
      <input id={id} type={shown ? 'text' : 'password'} value={value} placeholder={placeholder} autoComplete={autoComplete}
        autoCapitalize="none" autoCorrect="off" spellCheck={false} onChange={(e) => onChange(e.target.value)} />
      <button type="button" className="icon-btn" onClick={() => setShown(!shown)} aria-label={shown ? 'Hide' : 'Show'}>
        <Icon name={shown ? 'eyeoff' : 'eye'} />
      </button>
    </div>
  );
}
